import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
    Chip,
    Grid,
    Button,
    IconButton,
    CircularProgress,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { Icon as MdiIcon } from '@mdi/react';
import { mdiClipboardOutline } from '@mdi/js';
import { SplitButton } from '@commonClient2/components';
import { useOrdinalSuffix } from '@commonClient2/hooks';
import {
    BidProcessType,
    EmployeeBiddingStatus,
    UkgDateFormat,
    ShiftlineSelectionModals,
    EmployeeShiftLineTemplateAction,
} from '../../Enums';
import { getReportOptions } from '../../Employee/services/utilities/reports';
import { getProfileStatusLabel } from '../../Enums/Helpers/LabellingHelper';
import { getProfileStatusColor, getSequentialBannerProps } from '../../Enums/Helpers/StylingHelper';
import Banner from '../Banner/Banner';
import EmployeeInfoDesktop from '../EmployeeInfo/EmployeeInfoDesktop';
import GroupedIconButton from '../GroupedIconButton/GroupedIconButton';
import ImportWarning from '../ImportWarning/ImportWarning';
// eslint-disable-next-line no-unused-vars
import DateFormatService from '../../services/DateFormatService';
import { getSelectedShiftlineCount } from './ShiftlineSelectionHeader.services';
import './ShiftlineSelectionHeader.scss';

/**
 * @typedef {Object} SequentialBannerProps
 * @property {string} bannerColor
 * @property {string} fontColor
 * @property {string} bannerLabel
 * @property {boolean} hasTimer
 * @property {Date|null} sourceEndTime
 * @property {string} timerEndedText
 * @property {string} size
 *
 * @param {{
 *  isOnBehalf?: boolean,
 *  isSelectionDirty: boolean,
 *  setShowModal: Function,
 *  setShowReport: Function,
 *  handleBack: Function,
 *  handleTemplateAction?: Function,
 * }} props
 */
function ShiftlineSelectionHeader({
    isSelectionDirty,
    setShowModal,
    setShowReport,
    handleBack,
    isOnBehalf = false,
    handleTemplateAction = () => { },
}) {
    const { t } = useTranslation('shiftBidding');
    const { t: tEmployee } = useTranslation('shiftBidding', { keyPrefix: 'employee' });

    // @ts-ignore
    const { employeeBidProfile, employeeBidSelection } = useSelector((state) => state);

    const {
        name,
        bidProcessType,
        employeeRank,
        employeeSubmissionStatus,
        isEmployeeCanDeclineBid,
        isEnableEmployeeBidResultReport,
        isEnableEmployeeRankReport,
        hasTemplate,
        isRebidAllowed,
        submissionPeriodStartDate,
        submissionPeriodEndDate,
        requestPeriodStartDate,
        requestPeriodEndDate,
        isForeverRequestPeriod,
        status,
        warnings,
        isEmployeeCanBidAfterAllottedTime,
        isGroupShiftlinesWithSameSchedules
    } = employeeBidProfile;

    const isSequential = bidProcessType === BidProcessType.Sequential;
    const isUnsubmitted = employeeSubmissionStatus === EmployeeBiddingStatus.Unsubmitted;

    const sequentialBannerProps = isSequential ? getSequentialBannerProps(employeeBidProfile, false) : undefined;

    const linesSelected = getSelectedShiftlineCount(
        employeeBidSelection.items,
        employeeBidSelection.employeeSelections,
        isGroupShiftlinesWithSameSchedules
    );
    const progressTotal = employeeRank.rank;
    const selectionProgressLabel = isSequential ? `${linesSelected} / 1` : `${linesSelected} / ${progressTotal}`;
    const selectionProgressPercentageValue = isSequential
        ? (linesSelected / 1) * 100
        : (linesSelected / progressTotal) * 100;

    /**
     * @type {import("../../components/GroupedIconButton/GroupedIconButton").GroupedIconButtonOption[]}}
     */
    const reportOptions = getReportOptions({
        t, isSequential, isEnableEmployeeRankReport, isEnableEmployeeBidResultReport, setShowReport, employeeId: employeeRank.employeeId,
    });

    const requestPeriodLabel = tEmployee('requestPeriod');
    const requestStart = DateFormatService.formatDateOrEmpty(requestPeriodStartDate, UkgDateFormat.LongDate);
    const requestEnd = isForeverRequestPeriod 
        ? tEmployee('anytime') 
        : DateFormatService.formatDateOrEmpty(requestPeriodEndDate, UkgDateFormat.LongDate);
    const requestPeriod = `${requestStart} - ${requestEnd}`;

    const submissionStartLabel = tEmployee('submissionStartDate');
    const submissionEndLabel = tEmployee('submissionEndDate');
    const submissionStart = DateFormatService.formatDateOrEmpty(submissionPeriodStartDate, UkgDateFormat.LongDateTime);
    const submissionEnd = DateFormatService.formatDateOrEmpty(submissionPeriodEndDate, UkgDateFormat.LongDateTime);

    const employeeRankAsOrdinal = useOrdinalSuffix(employeeRank.rank);

    return (
        <>
            {isSequential && sequentialBannerProps && (
                <Banner
                    bannerColor={sequentialBannerProps.bannerColor}
                    fontColor={sequentialBannerProps.fontColor}
                    bannerLabel={sequentialBannerProps.bannerLabel}
                    hasTimer={sequentialBannerProps.hasTimer}
                    sourceEndTime={sequentialBannerProps.sourceEndTime}
                    timerEndedText={sequentialBannerProps.timerEndedText}
                    isEmployeeCanBidAfterAllottedTime={isEmployeeCanBidAfterAllottedTime}
                    size="lg"
                />
            )}
            <Grid
                container
                className="slh-selection-topbar"
            >
                <Grid
                    item
                    xs={6}
                    lg={6}
                    className="header-properties-container"
                >
                    <Grid
                        container
                        className="header-properties-line-container"
                    >
                        <Grid
                            item
                        >
                            <Grid
                                container
                            >
                                <Grid
                                    item
                                    md={3}
                                    lg={4}
                                >
                                    <div
                                        className="header-title-container"
                                    >
                                        <IconButton
                                            className="header-icon-button"
                                            onClick={isSelectionDirty 
                                                ? () => setShowModal(isOnBehalf ? ShiftlineSelectionModals.BackOnBehalf : ShiftlineSelectionModals.Back) 
                                                : () => handleBack()}
                                        >
                                            <FontAwesomeIcon icon={faArrowLeft} />
                                        </IconButton>
                                        <h5
                                            className="bid-name"
                                        >
                                            {name}
                                        </h5>
                                    </div>
                                </Grid>
                                <Grid
                                    item
                                    md={3}
                                    lg={4}
                                    className="header-status-container"
                                >
                                    <Chip
                                        label={getProfileStatusLabel(status)}
                                        variant="outlined"
                                        style={getProfileStatusColor(status)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                        >
                            <Grid
                                container
                            >
                                <Grid
                                    item
                                    md={3}
                                    lg={4}
                                >
                                    <span className="header-property-label">{`${requestPeriodLabel} : `}</span>
                                    <span className="header-property-value">{requestPeriod}</span>
                                </Grid>
                                <Grid
                                    item
                                    md={3}
                                    lg={4}
                                >
                                    <span className="header-property-label">{`${submissionStartLabel} : `}</span>
                                    <span className="header-property-value">{submissionStart}</span>
                                </Grid>
                                <Grid
                                    item
                                    md={3}
                                    lg={4}
                                >
                                    <span className="header-property-label">{`${submissionEndLabel} : `}</span>
                                    <span className="header-property-value">{submissionEnd}</span>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    xs={6}
                    lg={6}
                >
                    <Grid
                        container
                        spacing={0}
                        justifyContent="flex-end"
                        alignItems="center"
                        className="buttons-container"
                    >
                        {
                            isRebidAllowed && (
                                <ImportWarning
                                    warnings={warnings}
                                    isMini={false}
                                />
                            )
                        }
                        <EmployeeInfoDesktop employeeBidProfile={employeeBidProfile} />
                        {
                            !isOnBehalf && !!(reportOptions.length) && (
                                <GroupedIconButton
                                    icon={(
                                        <MdiIcon
                                            path={mdiClipboardOutline}
                                            size={1}
                                        />
                                    )}
                                    buttonClassName="ssh-report-button"
                                    tooltipTitle={t('common.reports.reports')}
                                    options={reportOptions}
                                />
                            )
                        }
                        {
                            isUnsubmitted && !isSequential && !isOnBehalf && (
                                <Grid
                                    item
                                >
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => setShowModal(ShiftlineSelectionModals.Close)}
                                    >
                                        {tEmployee('common.close')}
                                    </Button>
                                </Grid>
                            )
                        }
                        {
                            isRebidAllowed && !isOnBehalf && (
                                <Grid
                                    item
                                >
                                    <SplitButton
                                        options={[
                                            {
                                                label: tEmployee('common.saveTemplate'),
                                                value: EmployeeShiftLineTemplateAction.Save,
                                                disabled: false,
                                            },
                                            ...(
                                                hasTemplate ? [{
                                                    label: tEmployee('common.loadTemplate'),
                                                    value: EmployeeShiftLineTemplateAction.Load,
                                                    disabled: false,
                                                }] : []
                                            ),
                                        ]}
                                        color="secondary"
                                        handleClick={(/** @type {ISplitButton.SplitButtonOption} */ option) => handleTemplateAction(option)}
                                        disabled={false}
                                    />
                                </Grid>
                            )
                        }
                        <Grid
                            item
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowModal(ShiftlineSelectionModals.ConfirmBidSelection)}
                            >
                                {tEmployee('shiftlineSelection.submit')}
                            </Button>
                            {
                                isEmployeeCanDeclineBid
                                && (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => setShowModal(ShiftlineSelectionModals.DeclineBid)}
                                    >
                                        {tEmployee('shiftlineSelection.decline')}
                                    </Button>
                                )
                            }
                        </Grid>

                        <Grid
                            item
                        >
                            <div className="selection-progress-container">
                                <div className="selection-rank-label-container">
                                    <div>
                                        {`${selectionProgressLabel} ${tEmployee('common.linesSelected', { count: linesSelected })}`}
                                    </div>
                                    <div>
                                        {`${tEmployee('common.youAreRanked')} ${employeeRankAsOrdinal}`}
                                    </div>
                                </div>
                                <div className="circular-progress-container">
                                    <CircularProgress
                                        size={32}
                                        variant="determinate"
                                        value={selectionProgressPercentageValue}
                                    />
                                    <CircularProgress
                                        size={32}
                                        variant="determinate"
                                        color="inherit"
                                        value={100}
                                    />
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}

export default ShiftlineSelectionHeader;
